import type { DisposalSet } from '@neptune/cx/utils';
import { initAttachmentField } from './attachments';
import { initRichTextField } from './rich-text';

/** The thread page carries one of these under the question and under every answer. */
export const REPLY_COMPOSER_SELECTOR = 'details[data-chroa-reply-form]';

/**
 * Starts a reply composer's editor and attachment control when it first opens, rather than every
 * composer on the thread page at load.
 *
 * Both initialisers skip a field they have already started, so each route below may fire more
 * than once.
 */
export const initReplyComposer = (composerEl: HTMLDetailsElement, gc: DisposalSet): void => {
    const start = () => {
        composerEl.querySelectorAll<HTMLElement>('[data-chroa-rich-text]').forEach((fieldEl) => {
            initRichTextField(fieldEl, gc);
        });

        composerEl.querySelectorAll<HTMLElement>('[data-chroa-attachments]').forEach((fieldEl) => {
            initAttachmentField(fieldEl, gc);
        });
    };

    // Left open by the server for a deep link or a refilled draft, or opened by a click that
    // landed before this script ran.
    if (composerEl.open) {
        start();

        return;
    }

    // A summary toggles its details only once the click has been dispatched, so the editor is in
    // place before the composer first paints open.
    const summaryEl = composerEl.querySelector<HTMLElement>(':scope > summary');

    if (summaryEl) {
        gc.register(summaryEl).addListener('click', start);
    }

    // On the proxy button itself, so this runs before thread/detail.jsp's document-level handler
    // opens the composer and puts the caret in its editor.
    if (composerEl.id) {
        const triggerEls = document.querySelectorAll<HTMLElement>(
            `[data-chroa-open-form="${CSS.escape(composerEl.id)}"]`,
        );

        triggerEls.forEach((triggerEl) => {
            gc.register(triggerEl).addListener('click', start);
        });
    }

    // Anything else that opens it, such as find-in-page expanding it to show a match.
    gc.register(composerEl).addListener('toggle', () => {
        if (composerEl.open) start();
    });
};
